import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, Link as MuiLink, Stack, Typography, alpha } from '@mui/material';
import type {
  TeamsDisplayBlockNode,
  TeamsDisplayInlineNode,
  TeamsDisplayMarks,
} from '@atlas/shared';
import { TeamsFileAttachment } from './TeamsFileAttachment';

/**
 * Renders a Teams message from DATA — one component per node type.
 *
 * The whole point of the display-node pipeline is that message content authored
 * by a third party (including people outside Chauvet) can never become markup.
 * `dangerouslySetInnerHTML` must never appear in this file, and a `<b>` a sender
 * typed literally must reach the screen as those four characters.
 */

/** Tallest an inline image is allowed to be, so one photo cannot own the panel. */
const IMAGE_MAX_HEIGHT_PX = 240;

/** Shared text behaviour: the bubble is as narrow as 280px, so nothing may widen it. */
const textFlowSx = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
} as const;

function MarkedText({ node }: { node: TeamsDisplayMarks & { text: string } }) {
  // Marks nest as real elements — `<strong>`/`<em>`/`<u>`/`<s>` carry meaning to
  // assistive tech that a font-weight style would not.
  let content = <>{node.text}</>;
  if (node.code) {
    content = (
      <Box
        component="code"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.85em',
          px: 0.5,
          borderRadius: 0.5,
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.12),
        }}
      >
        {content}
      </Box>
    );
  }
  if (node.strike) content = <s>{content}</s>;
  if (node.underline) content = <u>{content}</u>;
  if (node.italic) content = <em>{content}</em>;
  if (node.bold) content = <strong>{content}</strong>;
  return content;
}

function InlineLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      // Inherit the bubble's colour so the link stays legible on both the self
      // (primary) and other (grey) backgrounds; the underline is what makes it
      // identifiable, so it never depends on colour alone.
      sx={{ color: 'inherit', textDecoration: 'underline', overflowWrap: 'anywhere' }}
    >
      {children}
    </MuiLink>
  );
}

function MentionChip({ text, isSelf }: { text: string; isSelf?: boolean }) {
  const { t } = useTranslation();
  // "Was I mentioned?" is the entire reason mentions exist, so a self-mention is
  // a real `<mark>` with its own accessible name — not merely a heavier colour.
  return (
    <Box
      component={isSelf ? 'mark' : 'span'}
      aria-label={isSelf ? t('teams.panel.mentionedYou', { name: text }) : undefined}
      sx={{
        display: 'inline',
        px: 0.5,
        borderRadius: 0.5,
        color: 'inherit',
        fontWeight: isSelf ? 700 : 500,
        bgcolor: (theme) => alpha(theme.palette.text.primary, isSelf ? 0.24 : 0.1),
      }}
    >
      {text}
    </Box>
  );
}

function InlineImage({
  src,
  alt,
  width,
  height,
}: {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const label = alt?.trim() || t('teams.panel.image');

  if (hasFailed) {
    return (
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontStyle: 'italic' }}>
        {t('teams.panel.imageFailed')}
      </Typography>
    );
  }

  return (
    <>
      <Box
        component="img"
        src={src}
        alt={label}
        loading="lazy"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
        onError={() => setHasFailed(true)}
        sx={{
          display: 'block',
          my: 0.5,
          maxWidth: '100%',
          maxHeight: IMAGE_MAX_HEIGHT_PX,
          borderRadius: 1,
          cursor: 'pointer',
          // The intrinsic size is only a hint: it reserves the right shape while
          // the bytes load, so the thread does not jump under the reader.
          width: width ? Math.min(width, IMAGE_MAX_HEIGHT_PX * 2) : undefined,
          aspectRatio: width && height ? `${width} / ${height}` : undefined,
          objectFit: 'contain',
        }}
      />
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="lg">
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh' }}
        />
      </Dialog>
    </>
  );
}

function InlineNodes({ nodes }: { nodes: TeamsDisplayInlineNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        switch (node.type) {
          case 'text':
            return <MarkedText key={index} node={node} />;
          case 'link':
            return (
              <InlineLink key={index} href={node.href}>
                <MarkedText node={{ ...node, text: node.text }} />
              </InlineLink>
            );
          case 'mention':
            return <MentionChip key={index} text={node.text} isSelf={node.isSelf} />;
          case 'image':
            return (
              <InlineImage
                key={index}
                src={node.src}
                alt={node.alt}
                width={node.width}
                height={node.height}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}

/**
 * Context a node needs but cannot carry: an attachment has to name its own
 * message to be downloadable, and the display tree deliberately knows nothing
 * about which conversation it came from.
 */
interface NodeContext {
  chatId?: string;
  messageId?: string;
  filesAvailable?: boolean;
}

function BlockNode({ node, context }: { node: TeamsDisplayBlockNode; context: NodeContext }) {
  const { t } = useTranslation();

  switch (node.type) {
    case 'paragraph':
      return (
        <Typography variant="body2" sx={textFlowSx}>
          <InlineNodes nodes={node.children} />
        </Typography>
      );

    case 'quote':
      return (
        <Typography
          variant="body2"
          component="blockquote"
          sx={{
            ...textFlowSx,
            m: 0,
            pl: 1,
            borderLeft: 3,
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.3),
            opacity: 0.9,
          }}
        >
          <InlineNodes nodes={node.children} />
        </Typography>
      );

    case 'codeBlock':
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            whiteSpace: 'pre',
            // Its own scroller: a single long line must never widen the panel.
            overflowX: 'auto',
            maxWidth: '100%',
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.12),
          }}
        >
          <code>{node.text}</code>
        </Box>
      );

    case 'list':
      return (
        <Box
          component={node.ordered ? 'ol' : 'ul'}
          sx={{ m: 0, pl: 2.5, '& li': { ...textFlowSx } }}
        >
          {node.items.map((item, index) => (
            <Typography key={index} component="li" variant="body2">
              <InlineNodes nodes={item.children} />
            </Typography>
          ))}
        </Box>
      );

    case 'card':
      // A card with only a title, or only text, still has to look deliberate —
      // this block is what stops bot messages and link unfurls rendering blank.
      return (
        <Box
          sx={{
            border: 1,
            borderRadius: 1,
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.25),
            p: 1,
            minWidth: 0,
          }}
        >
          {/* Inline nodes, not strings: cards author their text in a markdown
              subset, so a card's title is frequently itself a link. */}
          {node.title?.length ? (
            <Typography variant="subtitle2" sx={textFlowSx}>
              <InlineNodes nodes={node.title} />
            </Typography>
          ) : null}
          {node.text?.length ? (
            <Typography variant="body2" sx={{ ...textFlowSx, mt: node.title?.length ? 0.25 : 0 }}>
              <InlineNodes nodes={node.text} />
            </Typography>
          ) : null}
          {node.href ? (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption">
                <InlineLink href={node.href}>{t('teams.panel.cardOpen')}</InlineLink>
              </Typography>
            </Box>
          ) : null}
          {!node.title?.length && !node.text?.length && !node.href ? (
            <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
              {t('teams.panel.cardEmpty')}
            </Typography>
          ) : null}
        </Box>
      );

    case 'file':
      return (
        <TeamsFileAttachment
          node={node}
          chatId={context.chatId}
          messageId={context.messageId}
          filesAvailable={context.filesAvailable}
        />
      );

    case 'attachmentRef':
      // The backend resolves these away. One arriving here means something
      // upstream failed, and a blank beats a stray id on screen.
      return null;

    default:
      return null;
  }
}

export interface TeamsMessageContentProps {
  nodes: TeamsDisplayBlockNode[];
  /** Passed through to attachments so they can resolve their own download. */
  chatId?: string;
  messageId?: string;
  filesAvailable?: boolean;
}

export function TeamsMessageContent({
  nodes,
  chatId,
  messageId,
  filesAvailable,
}: TeamsMessageContentProps) {
  const context: NodeContext = { chatId, messageId, filesAvailable };
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      {nodes.map((node, index) => (
        <Fragment key={index}>
          <BlockNode node={node} context={context} />
        </Fragment>
      ))}
    </Stack>
  );
}
